import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { KustomerNote } from '@/types/kustomerNote';
import RepositoryResponse from '@/types/repositoryResponse';
import { Tag } from '@/types/tag';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';

export const kustomerNoteRepository = {
  async getItemConversationNotes({
    host,
    itemId
  }: {
    host: string;
    itemId: string;
  }): Promise<RepositoryResponse<KustomerNote[]>> {
    try {
      const path = `/api/dugout/notes/by_item/${itemId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to get task conversation notes.');
    }
  },

  async getKustomerNote({ host, noteId }: { host: string; noteId: string }): Promise<KustomerNote> {
    const path = `/api/dugout/notes/${noteId}/`;
    const { data } = await ajax.get({
      url: `${host}${path}`
    });

    return data;
  },

  async tags({ host, noteId }: { host: string; noteId: string }): Promise<{ tags: Tag[] } | void> {
    const path = `/api/dugout/notes/${noteId}/tags/`;
    const { data } = await ajax.get({
      url: `${host}${path}`
    });

    return data;
  },

  async kustomerConversationId({
    host,
    noteId
  }: {
    host: string;
    noteId: string;
  }): Promise<{ kustomer_id: string } | void> {
    const path = `/api/dugout/notes/${noteId}/kustomer_conversation_id/`;
    const { data } = await ajax.get({
      url: `${host}${path}`
    });

    return data;
  }
};

const KustomerNoteRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getItemConversationNotes(itemId: string): Promise<RepositoryResponse<KustomerNote[]>> {
      return kustomerNoteRepository.getItemConversationNotes({ host, itemId });
    },

    async getKustomerNote(noteId: string): Promise<KustomerNote> {
      return kustomerNoteRepository.getKustomerNote({ host, noteId });
    },

    async tags(noteId: string): Promise<{ tags: Tag[] } | void> {
      return kustomerNoteRepository.tags({ host, noteId });
    },

    async kustomerConversationId(noteId: string): Promise<{ kustomer_id: string } | void> {
      return kustomerNoteRepository.kustomerConversationId({ host, noteId });
    }
  };
};

export default KustomerNoteRepository;
